"use client";

import React from 'react';
import MenuButton from './MenuButton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

type Props = {
  title: string;
  centered?: boolean;
};

export default function PageHeaderClient({ title }: Props) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const appBarRef = React.useRef<HTMLDivElement | null>(null);
  const toggleDrawer = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('site-drawer-toggle'));
    }
  };

  React.useEffect(() => {
    const updateOffset = () => {
      const height = appBarRef.current?.getBoundingClientRect().height;
      if (!height) return;
      document.documentElement.style.setProperty('--header-offset', `${Math.ceil(height)}px`);
    };

    updateOffset();
    window.addEventListener('resize', updateOffset);
    return () => window.removeEventListener('resize', updateOffset);
  }, [title, isMdUp]);

  return (
    <>
      <AppBar
        ref={appBarRef}
        position="fixed"
        sx={{
          backgroundColor: '#0d47a1',
          height: { xs: 'auto', md: 'var(--header-height)' },
        }}
      >
        <Toolbar
          sx={{
            minHeight: 'var(--header-height)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: { xs: 1.5, md: 0 },
          }}
        >

          <MenuButton onClick={toggleDrawer} />

          <Box
            sx={(theme) => ({
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            })}
          >
            <Typography
              component="div"
              variant="h1"
              sx={{
                m: 0,
                fontSize: { xs: 'clamp(1.3rem, 6.2vw, 1.8rem)', sm: '1.7rem', md: '2rem' },
                fontWeight: 500,
                lineHeight: 1.2,
                color: 'inherit',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {title}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar sx={{ minHeight: 'var(--header-offset, var(--header-height))' }} />
    </>
  );
}
